"use client";

import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useMemo,
} from "react";
import { useAuth } from "@/lib/auth/auth-context";
import { isAdmin } from "@/lib/auth/role-helpers";
import { getMyPermissions } from "@/lib/api/permissions";
import type { EffectivePermissions, ModuleKey } from "@/lib/api/types";

/**
 * A module requirement: one key, or a list of keys of which ANY grants access.
 * The list form exists for pages served by more than one module — the
 * standalone Strategies page is open to `strategies` and to `trading_signals`.
 */
export type ModuleRequirement = ModuleKey | readonly ModuleKey[];

interface PermissionsContextType {
  permissions: EffectivePermissions | null;
  isLoading: boolean;
  hasModule: (key: ModuleRequirement) => boolean;
  hasPlant: (id: number) => boolean;
  hasStation: (id: number) => boolean;
  hasEVIProducer: (id: number) => boolean;
  hasEVIConsumer: (id: number) => boolean;
  refreshPermissions: () => Promise<void>;
}

const PermissionsContext = createContext<PermissionsContextType | undefined>(
  undefined
);

interface PermissionsProviderProps {
  children: React.ReactNode;
}

export function PermissionsProvider({ children }: PermissionsProviderProps) {
  const { user, isAuthenticated } = useAuth();
  const [permissions, setPermissions] = useState<EffectivePermissions | null>(
    null
  );
  const [isLoading, setIsLoading] = useState(true);

  const userIsAdmin = isAdmin(user);

  const fetchPermissions = useCallback(async () => {
    if (!isAuthenticated) {
      setPermissions(null);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    try {
      const data = await getMyPermissions();
      setPermissions(data);
    } catch {
      setPermissions(null);
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    fetchPermissions();
  }, [fetchPermissions]);

  const hasModule = useCallback(
    (key: ModuleRequirement): boolean => {
      if (userIsAdmin) return true;
      if (!permissions) return false;
      const keys: readonly ModuleKey[] = typeof key === "string" ? [key] : key;
      return keys.some((k) => permissions.modules.includes(k));
    },
    [userIsAdmin, permissions]
  );

  const hasPlant = useCallback(
    (id: number): boolean => {
      if (userIsAdmin) return true;
      if (!permissions) return false;
      return permissions.plant_ids.includes(id);
    },
    [userIsAdmin, permissions]
  );

  const hasStation = useCallback(
    (id: number): boolean => {
      if (userIsAdmin) return true;
      if (!permissions) return false;
      return permissions.charging_station_ids.includes(id);
    },
    [userIsAdmin, permissions]
  );

  const hasEVIProducer = useCallback(
    (id: number): boolean => {
      if (userIsAdmin) return true;
      if (!permissions) return false;
      return permissions.evi_producer_ids.includes(id);
    },
    [userIsAdmin, permissions]
  );

  const hasEVIConsumer = useCallback(
    (id: number): boolean => {
      if (userIsAdmin) return true;
      if (!permissions) return false;
      return permissions.evi_consumer_ids.includes(id);
    },
    [userIsAdmin, permissions]
  );

  const refreshPermissions = useCallback(async () => {
    await fetchPermissions();
  }, [fetchPermissions]);

  const value = useMemo<PermissionsContextType>(
    () => ({
      permissions,
      isLoading,
      hasModule,
      hasPlant,
      hasStation,
      hasEVIProducer,
      hasEVIConsumer,
      refreshPermissions,
    }),
    [
      permissions,
      isLoading,
      hasModule,
      hasPlant,
      hasStation,
      hasEVIProducer,
      hasEVIConsumer,
      refreshPermissions,
    ]
  );

  return (
    <PermissionsContext.Provider value={value}>
      {children}
    </PermissionsContext.Provider>
  );
}

export function usePermissions(): PermissionsContextType {
  const context = useContext(PermissionsContext);
  if (context === undefined) {
    throw new Error(
      "usePermissions must be used within a PermissionsProvider"
    );
  }
  return context;
}
